/* eslint-disable max-lines-per-function */
/* eslint-disable indent */
import {ITEM_CONDITIONS} from 'extensions/rma/data/constants';
import 'extensions/ui-components/x-file-uploader';
import 'extensions/ui-components/x-message';
import {defineCustomElement} from 'extensions/ui-utils';
import {html, LitElement, nothing} from 'lit';

export class RMAProductReturnData extends LitElement {
	static get properties () {
		return {
			index: {
				type: Number
			},
			itemCondition: {
				attribute: 'item-condition',
				type: String
			},
			reason: {
				type: String
			},
			reasons: {
				type: Array
			},
			serialNumber: {
				attribute: 'serial-number',
				type: String
			},
			serialNumbers: {
				attribute: 'serial-numbers',
				type: Array
			},
			showIndexLabel: {
				attribute: 'show-index-label',
				type: Boolean
			},
			showSerialNumbers: {
				attribute: 'show-serial-numbers',
				type: Boolean
			},
			sku: {
				type: String
			},
			uploadData: {
				type: Array
			}
		};
	}

	createRenderRoot () {
		return this;
	}

	get state () {
		return {
			index: parseInt(this.index, 10),
			itemCondition: this.itemCondition,
			reason: this.reason,
			serialNumber: this.serialNumber,
			uploadData: this.uploadData,
			// eslint-disable-next-line array-element-newline, no-extra-parens
			valid: this.renderRoot?.querySelector('form')?.checkValidity() && (!['DAM', 'MSS'].includes(this.reason) || (['DAM', 'MSS'].includes(this.reason) && this.uploadData?.length > 0))
		};
	}

	constructor () {
		super();

		/**
		 * @type {number}
		 */
		this.index = 0;

		/**
		 * @type {string}
		 */
		this.itemCondition = '';

		/**
		 * @type {string}
		 */
		this.reason = '';

		/**
		 * @type {import('extensions/rma/types').Reason[]}
		 */
		this.reasons = [];

		/**
		 * @type {string}
		 */
		this.serialNumber = '';

		/**
		 * @type {string[]}
		 */
		this.serialNumbers = [];

		/**
		 * @type {boolean}
		 */
		this.showIndexLabel = false;

		/**
		 * @type {boolean}
		 */
		this.showSerialNumbers = false;

		/**
		 * @type {string}
		 */
		this.sku = '';

		/**
		 * @type {import('extensions/rma/types').RMAUploadData}
		 */
		this.uploadData = [];
	}

	dispatchStateChange () {
		this.dispatchEvent(new CustomEvent('return-data:statechange', {
			bubbles: true,
			composed: true,
			detail: this.state
		}));
	}

	async handleSerialNumberChange (e) {
		this.serialNumber = e.target.value;

		await this.updateComplete;

		this.dispatchStateChange();
	}


	async handleReasonChange (e) {
		this.reason = e.target.value;

		// Set new default values for paired properties
		this.itemCondition = ITEM_CONDITIONS?.length > 0
			? ITEM_CONDITIONS?.[0].code
			: '';
		this.uploadData = [];

		const uploadPhotosLabel = this.renderRoot.querySelector('.upload-photos-label');

		if (['DAM', 'MSS'].includes(this.reason)) {
			uploadPhotosLabel.classList.add('required');
		} else {
			uploadPhotosLabel.classList.remove('required');
		}

		await this.updateComplete;

		this.dispatchStateChange();
	}

	async handleItemConditionChange (e) {
		this.itemCondition = e.target.value;

		await this.updateComplete;

		this.dispatchStateChange();
	}

	async handleUploadChange (e) {
		this.uploadData = e.detail;

		await this.updateComplete;

		this.dispatchStateChange();
	}

	renderSerialNumber () {
		if (this.showSerialNumbers) {
			return html`
				<div class="c-form-list__item">
					<label class="form-label required" for="l-${this.index}-serial-number">Serial Number</label>
					${
						this.serialNumbers.length === 0
							? html`
								<x-message type="error">Unable to additional serial number(s). Please reduce quantity or contact customer support.</x-message>
							`
							: nothing
					}
					<div class="c-form-select">
						<select class="c-form-select__dropdown" id="l-${this.index}-serial-number" required @change="${this.handleSerialNumberChange}">
							<option ?selected="${!this.serialNumber}" disabled value="">Select Serial Number</option>
							${this.serialNumbers?.map(serialNumber => html`<option value="${serialNumber}" ?selected="${serialNumber === this.serialNumber}">${serialNumber}</option>`)}
						</select>
					</div>
				</div>
			`;
		}

		return nothing;
	}

	/**
	 * @param {import('extensions/rma/types').ItemCondition} itemCondition
	 */
	renderItemCondition (itemCondition) {
		return html`
			<label class="c-form-checkbox c-form-checkbox--radio">
				<input required name="radio" class="c-form-checkbox__input" type="radio" name="itemCondition" value="${itemCondition.code}" .checked="${this.itemCondition === itemCondition.code}" @change="${this.handleItemConditionChange}" />
				<div class="t-checkout-caption">
					<span class="c-form-checkbox__caption">
						<span class="t-checkout-caption__inner">
							${itemCondition.description}
						</span>
					</span>
				</div>
			</label>
		`;
	}

	renderItemConditions () {
		return html`
			<div class="c-form-list__item">
				<label class="form-label upload-photos-label">Upload Photos</label>

				${!this.uploadData || this.uploadData?.length === 0 ? html`<x-file-uploader class="uploader-button" @change="${this.handleUploadChange}"></x-file-uploader>` : this.uploadData?.map(uploadData => html`<div>${uploadData.name}</div>`)}
			</div>

			<div class="c-form-list__item">
					<div class="form-label required">Item Condition</div>

					${ITEM_CONDITIONS?.map((itemCondition, index) => this.renderItemCondition(itemCondition, index))}
			</div>
		`;
	}

	render () {
		return html`
			<div class="product-row">
				<div class="product-column-checkbox"></div>
				<div class="product-column-info product-column-return-data">
					${
						this.showIndexLabel
							? html`
								<div class="return-data-index-label">
									<span class="circle">${this.index + 1}</span>
									<span>${this.sku}</span>
								</div>
							` : nothing
					}

					<form @submit="${e => e.preventDefault()}">
						${this.renderSerialNumber()}

						<div class="c-form-list__item">
							<label class="form-label required" for="l-${this.index}-reason">Reason for Return</label>
							<div class="c-form-select">
								<select class="c-form-select__dropdown" id="l-${this.index}-reason" required @change="${this.handleReasonChange}">
									<option ?selected="${!this.reason}" disabled value="">Select Reason</option>
									${this.reasons?.map(reason => html`<option value="${reason.code}" ?selected="${reason.code === this.reason}">${reason.description}</option>`)}
								</select>
							</div>
						</div>

						${this.renderItemConditions()}
					</form>
				</div>
			</div>
		`;
	}
};

defineCustomElement('x-rma-return-data', RMAProductReturnData);
